import type { FileForProcessing } from '../Model/index.js'
import type { IContentFile } from '../Model/File/IContentFile.js'
import type { SampleConfigurationTypeKey } from '../Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from '../Generated/SampleConfigurationType.js'
import { detectReadLayout } from './orderSettingDetection/readLayoutDetection.js'

/** Whether a sample of this configuration is an R1/R2 pair, so a lone forward file means its R2 is missing. */
export const expectsReverseFile = (configuration: SampleConfigurationTypeKey): boolean =>
  configuration !== 'COMBINED_SAMPLE_PAIRS' && !SampleConfigurationTypes[configuration].impliesMerged

/** What a merged-to-paired switch leaves behind, and what the backend's attach repairs. */
export const lacksReverseFile = (item: FileForProcessing, configuration: SampleConfigurationTypeKey): boolean =>
  expectsReverseFile(configuration) && !item.isMerged && !!item.forwardFileId && !item.reverseFileId

/** An upload of the order as the files endpoint lists it; only uploads whose bytes arrived are offered. */
export type OrderUpload = { file: IContentFile & { verifiedAt?: string | null } }

export type ReverseFileCandidate = {
  fileId: string
  fileName: string
  /** The sample this file is the lone forward file of — the R2 became a sample of its own. Attaching absorbs it. */
  absorbSample?: { id: string; name?: string }
}

/**
 * The files a sample missing its R2 can take: uploads of the order no live sample uses, and the forward files of
 * other samples that are themselves missing their R2. Anything else would leave a file backing two samples.
 */
export const reverseFileCandidates = (
  target: FileForProcessing,
  uploads: readonly OrderUpload[],
  liveItems: readonly FileForProcessing[],
  configuration: SampleConfigurationTypeKey
): ReverseFileCandidate[] =>
  uploads.flatMap(({ file }): ReverseFileCandidate[] => {
    if (!file.verifiedAt || file.id === target.forwardFileId) {
      return []
    }
    const users = liveItems.filter((item) => item.forwardFileId === file.id || item.reverseFileId === file.id)
    if (users.length === 0) {
      return [{ fileId: file.id, fileName: file.fileName }]
    }
    const [holder] = users
    if (users.length === 1 && holder.id !== target.id && holder.forwardFileId === file.id && lacksReverseFile(holder, configuration)) {
      return [{ fileId: file.id, fileName: file.fileName, absorbSample: { id: holder.id, name: holder.customerSampleName } }]
    }
    return []
  })

export type ReversePairing = {
  sample: FileForProcessing
  fileId: string
  fileName: string
  absorbSampleId?: string
}

/**
 * Pairs samples missing their R2 with candidate files by the R1/R2 naming convention, the way an upload batch is
 * paired. A sample is only ever the R1 side: one whose own file reads as an R2 is the one absorbed instead.
 */
export const planReversePairingByName = (
  targets: readonly FileForProcessing[],
  candidatesFor: (target: FileForProcessing) => readonly ReverseFileCandidate[]
): ReversePairing[] => {
  type Entry = { fileId: string; fileName: string; holder?: FileForProcessing }
  const entries = new Map<string, Entry>()
  targets.forEach((target) => {
    if (target.forwardFile) {
      entries.set(target.forwardFileId, { fileId: target.forwardFileId, fileName: target.forwardFile.fileName, holder: target })
    }
  })
  const candidatesByTarget = new Map(targets.map((target) => [target.id, candidatesFor(target)]))
  candidatesByTarget.forEach((candidates) =>
    candidates.forEach((candidate) => {
      if (!entries.has(candidate.fileId)) {
        entries.set(candidate.fileId, { fileId: candidate.fileId, fileName: candidate.fileName })
      }
    })
  )

  const list = [...entries.values()]
  const { pairs } = detectReadLayout(list.map((entry) => ({ name: entry.fileName })))
  return pairs.flatMap(([first, second]): ReversePairing[] => {
    const forward = list[first]
    const reverse = list[second]
    const candidate = forward.holder && candidatesByTarget.get(forward.holder.id)?.find((c) => c.fileId === reverse.fileId)
    if (!forward.holder || !candidate) {
      return []
    }
    return [{ sample: forward.holder, fileId: reverse.fileId, fileName: reverse.fileName, absorbSampleId: candidate.absorbSample?.id }]
  })
}
